import React, { useEffect, useState, useCallback } from 'react';
import { Settings, Save } from 'lucide-react';
import apiClient from '../../utils/api';
import { useToast } from '../../context/ToastContext';

type Setting = { id: string; key: string; value: string; description: string; category: string };

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General', branding: 'Branding', security: 'Security',
  webhooks: 'Webhooks', billing: 'Billing',
};

export const SuperAdminSettings: React.FC = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.getSettings();
      const list: Setting[] = res.data.data ?? [];
      setSettings(list);
      const initial: Record<string, string> = {};
      list.forEach(s => { initial[s.key] = s.value ?? ''; });
      setEdits(initial);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.updateSettings(edits);
      toast('Settings saved successfully', 'success');
      load();
    } catch {
      toast('Failed to save settings', 'error');
    } finally { setSaving(false); }
  };

  const grouped = settings.reduce<Record<string, Setting[]>>((acc, s) => {
    const cat = s.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  const isDirty = settings.some(s => edits[s.key] !== (s.value ?? ''));

  return (
    <>
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Settings size={20} /> Platform Settings
        </h1>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || !isDirty}>
          <Save size={15} /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {isDirty && (
        <div className="card" style={{ background: '#fef9c3', border: '1px solid #fde047', marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: '#854d0e' }}>⚠ You have unsaved changes.</span>
        </div>
      )}

      {loading ? <div className="loading-spinner">Loading settings...</div> : (
        Object.entries(grouped).map(([category, items]) => (
          <div className="card" key={category} style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#374151', textTransform: 'capitalize' }}>
              {CATEGORY_LABELS[category] || category}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {items.map(s => (
                <div key={s.key} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, alignItems: 'start' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>{s.key}</div>
                    {s.description && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{s.description}</div>}
                  </div>
                  <div>
                    {s.key === 'maintenance_mode' ? (
                      <select value={edits[s.key] ?? s.value} onChange={e => setEdits(prev => ({ ...prev, [s.key]: e.target.value }))} style={{ width: '100%' }}>
                        <option value="false">Disabled</option>
                        <option value="true">Enabled</option>
                      </select>
                    ) : s.key === 'maintenance_message' ? (
                      <textarea value={edits[s.key] ?? s.value} onChange={e => setEdits(prev => ({ ...prev, [s.key]: e.target.value }))}
                        rows={2} style={{ width: '100%', resize: 'vertical' }} />
                    ) : (
                      <input type="text" value={edits[s.key] ?? s.value}
                        onChange={e => setEdits(prev => ({ ...prev, [s.key]: e.target.value }))}
                        style={{ width: '100%' }} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </>
  );
};
